import { feeMoneyCents, formatFeeAdjustmentAmount } from "../../fee-adjustment";
import { canUsePermission, type PermissionUser } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { financePayableOverviewQuery, financeReceivableOverviewQuery } from "../config";
import { financePaymentRequestListSearchParams } from "./financePaymentRequestService";
import type {
  FinancePayableOverviewFilters,
  FinancePayableRequestStatus,
  FinancePayableSettlementSummary,
  FinanceReceivableExcludedAgentCollectionItem,
  FinanceReceivableOutstandingKind,
  FinanceReceivableOverviewFilters,
  FinanceReceivableProjectSearchScope,
  FinanceReceivableReferenceQuote,
} from "../types";

/**
 * 确认件编辑页只分未定稿／已定稿，列表页叫草稿／已生效；财务要分清「定稿」与「全部应付已生效」，
 * 这里三态分开。未登记的新状态原样显示。
 */
const WORKSPACE_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: "未定稿",
  finalized: "已定稿",
  effective: "已生效",
};

/** 与确认件协议卡片同一叫法；未登记的新状态原样显示。 */
const AGREEMENT_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: "待盖章",
  effective: "已生效",
};

/** 与确认件模块文档同一说法：普通项目是供应商，住宿没有供应商时是酒店；未登记的新类型原样显示。 */
const SERVICE_PROVIDER_TYPE_LABELS: Readonly<Record<string, string>> = {
  supplier: "供应商",
  resource_spu: "酒店",
};

/** 确认件工作区状态的中文名。 */
export function financePayableWorkspaceStatusLabel(status: string): string {
  return WORKSPACE_STATUS_LABELS[status] ?? status;
}

/** 确认协议状态的中文名。 */
export function financePayableAgreementStatusLabel(status: string): string {
  return AGREEMENT_STATUS_LABELS[status] ?? status;
}

/** 真实服务对象类型的中文名。 */
export function financePayableServiceProviderTypeLabel(type: string): string {
  return SERVICE_PROVIDER_TYPE_LABELS[type] ?? type;
}

/** 数量与计价数去掉小数末尾的 0（2.00 → 2，1.50 → 1.5），显示效果与确认件页面一致；按字符串处理，不经过浮点数。 */
export function formatFinancePayableCount(value: string): string {
  return value.includes(".") ? value.replace(/\.?0+$/u, "") : value;
}

/** 协议当前应付与行合计之间有没有差额；按分比较，不经过浮点数。 */
export function hasFinancePayableUnattributedAmount(value: string): boolean {
  return feeMoneyCents(value) !== 0n;
}

/**
 * 结算主体行的「请款」按钮为什么点不了：可请额度为 0 或负数时给出原因，大于 0 时为空；按分比较，不经过浮点数。
 * 这里只管按钮，提交时后端还会锁行按那一刻的额度再拦一次。
 */
export function financePayableRequestBlockedReason(requestableAmount: string): string | null {
  const cents = feeMoneyCents(requestableAmount);
  return cents !== null && cents > 0n ? null : "没有可请额度，不能请款";
}

/** 金额显示为 ¥ 加千分位两位小数，减项带负号；全程不经过浮点数。 */
export function formatFinancePayableAmount(value: string | null): string {
  return formatFeeAdjustmentAmount(value, false);
}

/** 服务日期区间；工作区上没填的一端显示「未填」。 */
export function formatFinancePayableServiceDates(from: string | null, to: string | null): string {
  if (!from && !to) return "未填";
  return `${from ?? "未填"} 至 ${to ?? "未填"}`;
}

/** 行程上没有确认件工作区：这是一个团的正常状态，不是接口故障。 */
export function isFinancePayableWorkspaceNotFound(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "FINANCE_PAYABLE_WORKSPACE_NOT_FOUND";
}

/** 要看预付款的团（行程）不存在：多半是地址上的行程 ID 不对。 */
export function isFinancePayableItineraryNotFound(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "FINANCE_PAYABLE_ITINERARY_NOT_FOUND";
}

/** 工作区里既没有协议也没有未归口确认行。 */
export function isFinancePayableSummaryEmpty(summary: FinancePayableSettlementSummary): boolean {
  return summary.rows.length === 0 && summary.unassignedGroups.length === 0;
}

/** 与合同交接页标题左侧同一叫法；未登记的新状态原样显示。 */
const ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: "草稿",
  finalized: "定稿",
  formed: "已挂网",
};

/** 名单头状态；formed 是旧系统同步来的已成团，与定稿同样计应收。未登记的新状态原样显示。 */
const ROSTER_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: "草稿",
  finalized: "已定稿",
  formed: "已成团",
};

/** 与合同交接表「实际出团」行同一叫法；未登记的新分组原样显示。 */
const PRICE_TIER_GROUP_LABELS: Readonly<Record<string, string>> = {
  student: "学生",
  teacher: "老师",
  other: "其他",
  off_contract: "合同外",
};

/** 价格档分组的中文名。 */
export function financeReceivablePriceTierGroupLabel(group: string): string {
  return PRICE_TIER_GROUP_LABELS[group] ?? group;
}

/** 金额显示为 ¥ 加千分位两位小数，减项带负号，空值显示「暂无」；全程不经过浮点数。 */
export function formatFinanceReceivableAmount(value: string | null): string {
  return formatFeeAdjustmentAmount(value, false);
}

/**
 * 尚欠算不出时那一格写的原因：单团应收清单、应收列表共用一份，不写 0。
 * 导出由后端写（`finance_overview/excel.py` 的 `OUTSTANDING_REASONS`），两边文字要保持一致。
 */
export const FINANCE_RECEIVABLE_OUTSTANDING_REASONS: Readonly<
  Record<Exclude<FinanceReceivableOutstandingKind, "computed">, string>
> = {
  legacy_system: "旧系统收款",
  roster_unfinalized: "名单未定稿",
  quote_unreadable: "报价读不到",
};

/** 差额、未计入调整这类「有才显示」的数：空值与 0 都不显示；按分比较，不经过浮点数。 */
export function hasFinanceReceivableAmount(value: string | null): boolean {
  const cents = feeMoneyCents(value);
  return cents !== null && cents !== 0n;
}

/** 团编码在交接单定稿时才生成，之前显示「未生成」。 */
export function formatFinanceReceivableTeamId(teamId: string | null): string {
  return teamId ?? "未生成";
}

/** 交接单状态的中文名。 */
export function financeReceivableOrderStatusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status] ?? status;
}

/** 名单状态的中文名；订单还没有名单时明说。 */
export function financeReceivableRosterStatusLabel(status: string | null): string {
  if (status === null) return "还没有名单";
  return ROSTER_STATUS_LABELS[status] ?? status;
}

/** 有停用成员时提示他们没算进合计，这也是这里比名单页页脚少的原因。 */
export function financeReceivableStoppedMemberHint(count: number): string | null {
  return count > 0 ? `${count} 位停用成员未计入` : null;
}

/** 按订单 ID 找不到订单：地址里的订单 ID 不对，不是接口故障。 */
export function isFinanceReceivableOrderNotFound(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "FINANCE_RECEIVABLE_ORDER_NOT_FOUND";
}

/** 参考报价的来源。 */
export function financeReceivableReferenceQuoteSourceLabel(
  source: NonNullable<FinanceReceivableReferenceQuote["source"]>,
): string {
  return source === "deal" ? "成交报价" : "最新一版定稿报价";
}

/** 报价版本，与合同交接页选择成交报价同一写法；读不出来时显示「暂无」。 */
export function formatFinanceReceivableQuoteVersion(versionNo: number | null, versionName: string | null): string {
  if (versionNo === null) return "暂无";
  return versionName ? `V${versionNo} · ${versionName}` : `V${versionNo}`;
}

/** 名单被打回草稿时账本里可能已有完成的调整：有才提示，只给金额（按人给不出笔数）。 */
export function financeReceivablePendingAdjustmentHint(total: string): string | null {
  if (!hasFinanceReceivableAmount(total)) return null;
  return `已有完成的费用调整合计 ${formatFinanceReceivableAmount(total)}，名单定稿后计入`;
}

/** 代收类别与管它的那个开关，叫法与交接单表单一致。 */
const AGENT_COLLECTION_CATEGORIES: Readonly<
  Record<FinanceReceivableExcludedAgentCollectionItem["category"], { label: string; switchLabel: string }>
> = {
  train_ticket: { label: "火车票", switchLabel: "是否代收代付大交通" },
  flight_ticket: { label: "机票", switchLabel: "是否代收代付大交通" },
  intangible: { label: "非遗", switchLabel: "是否代收代付非遗校企" },
  school_enterprise: { label: "校企", switchLabel: "是否代收代付非遗校企" },
};

/** 未计入的代收的原因：交接单上填了金额，但对应开关选了否。 */
export function financeReceivableExcludedAgentCollectionReason(
  item: FinanceReceivableExcludedAgentCollectionItem,
): string {
  const { label, switchLabel } = AGENT_COLLECTION_CATEGORIES[item.category];
  return `交接单上填了${label} ${formatFinanceReceivableAmount(item.amount)}，但「${switchLabel}」选了否，未计入`;
}

/**
 * 项目搜索调的是定制项目列表接口，它要定制列表权限；没有就不给入口，只留团编码输入框。
 * 有「看全部定制」权限的搜全部项目，否则只搜自己作为销售或计调的。
 */
export function financeReceivableProjectSearchScope(
  user: PermissionUser | null | undefined,
): FinanceReceivableProjectSearchScope | null {
  if (!canUsePermission(user, "customization.project:list")) return null;
  return canUsePermission(user, "customization.project:list_all") ? "all" : "mine";
}

/**
 * 应付列表请款状态的中文名，筛选下拉按这个顺序列出。状态由后端按每家结算主体判好，前端只显示。
 * 「多请待退」只表示有一家已请款超过了它的当前应付，不代表已经多付、要退款或在追回，页面上另写一行说明。
 */
export const FINANCE_PAYABLE_REQUEST_STATUS_OPTIONS: ReadonlyArray<{ value: FinancePayableRequestStatus; label: string }> = [
  { value: "awaiting_request", label: "待请款" },
  { value: "in_progress", label: "请款进行中" },
  { value: "settled", label: "已结清" },
  { value: "over_requested", label: "多请待退" },
];

/** 应付列表请款状态的中文名。 */
export function financePayableRequestStatusLabel(status: FinancePayableRequestStatus): string {
  return FINANCE_PAYABLE_REQUEST_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

/** 筛选出的团超过候选上限：后端整次拒绝、不截断，页面提示加出团日期范围；财务总览几张列表共用。 */
export function isFinanceOverviewCandidateLimitExceeded(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "FINANCE_OVERVIEW_CANDIDATE_LIMIT_EXCEEDED";
}

/**
 * 导出时筛选出的团超过候选上限。导出按文件取，后端的错误体也是 Blob，拦截器归一后的错误里没有错误码，
 * 只能按 HTTP 状态判：财务总览接口的 400 只来自候选超限（参数错是 422，没权限是 401/403）。
 */
export function isFinanceOverviewExportLimitExceeded(error: unknown): boolean {
  return isHttpClientError(error) && error.statusCode === 400;
}

const OVERVIEW_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

/** 从地址读一个出团日期；不是 YYYY-MM-DD 的当没填，免得把坏值交给接口。 */
function overviewDateParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  return value !== null && OVERVIEW_DATE_PATTERN.test(value) ? value : null;
}

/**
 * 应收列表的筛选写进地址：用和接口参数同一份映射（`financeReceivableOverviewQuery`），参数名与接口一致，
 * 只写填了的，勾了「还有尚欠」写 `has_outstanding=true`。列表以地址为准：首次进入、刷新、页面开着时再跳过来
 * 都按地址取数；列表的缓存键也由它拼出，所以新条件自动进缓存键。
 */
export function financeReceivableOverviewSearchParams(filters: FinanceReceivableOverviewFilters): URLSearchParams {
  return new URLSearchParams(
    Object.entries(financeReceivableOverviewQuery(filters)).map(([key, value]) => [key, String(value)]),
  );
}

/** 从地址读应收列表的筛选；没写的为空，`has_outstanding` 只认 `true`。 */
export function readFinanceReceivableOverviewSearchParams(params: URLSearchParams): FinanceReceivableOverviewFilters {
  return {
    keyword: params.get("q") ?? "",
    departureFrom: overviewDateParam(params, "departure_from"),
    departureTo: overviewDateParam(params, "departure_to"),
    salesName: params.get("sales_name") ?? "",
    hasOutstanding: params.get("has_outstanding") === "true",
  };
}

/**
 * 应付列表的筛选写进地址：用和接口参数同一份映射（`financePayableOverviewQuery`），参数名与接口一致，
 * 只写填了的，勾了「还有可请额度」写 `has_requestable_amount=true`。工作台「还有可请额度的团」也用它拼地址，
 * 跳进来就是筛好的。
 */
export function financePayableOverviewSearchParams(filters: FinancePayableOverviewFilters): URLSearchParams {
  return new URLSearchParams(
    Object.entries(financePayableOverviewQuery(filters)).map(([key, value]) => [key, String(value)]),
  );
}

/** 从地址读应付列表的筛选；没写的为空，不认识的请款状态当没选。 */
export function readFinancePayableOverviewSearchParams(params: URLSearchParams): FinancePayableOverviewFilters {
  const requestStatus = params.get("request_status");
  return {
    keyword: params.get("q") ?? "",
    departureFrom: overviewDateParam(params, "departure_from"),
    departureTo: overviewDateParam(params, "departure_to"),
    dispatcherName: params.get("dispatcher_name") ?? "",
    requestStatus: FINANCE_PAYABLE_REQUEST_STATUS_OPTIONS.some((option) => option.value === requestStatus)
      ? (requestStatus as FinancePayableRequestStatus)
      : null,
    hasRequestableAmount: params.get("has_requestable_amount") === "true",
  };
}

/** 工作台上点得进去的地方：两张列表、筛好「还有可请额度」的应付列表、停在「待付款」的请款记录、待我审批。 */
export type FinanceWorkbenchTarget =
  | "receivableList"
  | "payableList"
  | "requestableItineraries"
  | "awaitingPayment"
  | "approvalTodo";

/** 带地址参数的地址：问号后为空时不带问号。 */
function withSearch(path: string, params: URLSearchParams): string {
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

/**
 * 工作台每一项点进去的地址。带筛选的两项用那一页自己读写地址的同一个函数拼，那一页以地址为准，
 * 所以首次进入、刷新、页面开着时再跳过来都是筛好的。
 */
export function financeWorkbenchTargetPath(target: FinanceWorkbenchTarget): string {
  switch (target) {
    case "receivableList":
      return "/finance/receivable-list";
    case "payableList":
      return "/finance/payable-list";
    case "requestableItineraries":
      return withSearch(
        "/finance/payable-list",
        financePayableOverviewSearchParams({
          ...readFinancePayableOverviewSearchParams(new URLSearchParams()),
          hasRequestableAmount: true,
        }),
      );
    case "awaitingPayment":
      return withSearch("/finance/payment-requests", financePaymentRequestListSearchParams("awaiting_payment"));
    case "approvalTodo":
      return "/approval/todo";
  }
}
